import { useEffect, useState, type CSSProperties } from 'react'
import { InView, type InviewProps } from '@dhl-ui/in-view'
import type { SystemCssProperties } from '@dhl-ui/theme'
import { BrokenImageIcon, PhotoIcon } from '@dhl-ui/icons'
import { Box } from '../../primitives/Box'
import { type DataTestIdAttribute } from '../../types/data-testid-attribute'

export type ImageProps = {
  /**
   * Aspect ratio of the image
   *
   */
  ratio: '16:9' | '4:3' | '1:1'

  /**
   * When your image is not following our 16*9 aspect ratio tell us where the focus is on your image
   * Supports standard keywords (e.g. 'center', 'top', 'bottom left', 'center bottom') or custom values (percentages/pixels).
   * @default 'center'
   */
  position?: CSSProperties['objectPosition']

  /**
   * Descriptive text for the image, used for accessibility.
   * @default ''
   */
  alt?: string | null

  /**
   * control when to load the image, useful for lazy loading images that are offscreen
   * @default 'eager'
   */
  loading?: 'eager' | 'lazy' | 'inView'
} & ({ src: string; srcSet?: never } | { src?: never; srcSet: string }) & {
    sx?: Pick<SystemCssProperties, 'mt' | 'mb' | 'my'>
  } & DataTestIdAttribute &
  Partial<Pick<InviewProps, 'rootMargin'>>

const minIcon = `min(28%, 40px)`

/**
 * Image
 * Image displays a source (using `src and/or `srcSet`) in a fixed aspect-ratio frame. It provides a placeholder while loading and a default when the source cannot be loaded. And also its loading strategy.
 *
 * Demos:
 * - [Image](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/constructs-image--docs)
 *
 */
export const Image = ({
  src,
  srcSet,
  alt,
  position = 'center',
  ratio,
  loading = 'eager',
  rootMargin,
  sx,
  ...rest
}: ImageProps) => {
  const [isBrokenImage, setIsBrokenImage] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsBrokenImage(false)
    setIsLoading(true)
  }, [src, srcSet])

  const frameSx = {
    ...sx,
    display: 'grid' as const,
    placeItems: 'center' as const,
    width: '100%',
    aspectRatio: ratio.replace(':', ' / '),
    overflow: 'hidden' as const,
    color: 'text.tertiary',
    backgroundColor: 'grey.300',
  }

  const image = (
    <img
      src={src}
      srcSet={srcSet}
      alt={alt ?? ''}
      loading={loading === 'lazy' ? 'lazy' : 'eager'}
      style={{
        gridArea: '1 / 1',
        zIndex: 1,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: position,
      }}
      onLoad={() => setIsLoading(false)}
      onError={() => {
        setIsBrokenImage(true)
        setIsLoading(false)
      }}
    />
  )

  return (
    <Box sx={frameSx} {...rest}>
      {isBrokenImage ? (
        <BrokenImageIcon sx={{ width: minIcon, height: 'auto' }} />
      ) : (
        <>
          {isLoading && <PhotoIcon sx={{ gridArea: '1 / 1', width: minIcon, height: 'auto' }} />}
          {loading === 'inView' ? (
            <Box sx={{ gridArea: '1 / 1', width: '100%', height: '100%' }}>
              <InView rootMargin={rootMargin} sx={{ width: '100%', height: '100%' }}>
                {image}
              </InView>
            </Box>
          ) : (
            image
          )}
        </>
      )}
    </Box>
  )
}
